import { useMemo, useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IMaterialsResponse, ISubscriptionResponse } from "@miplatform/types";
import { useSession } from "@mibuildweb/hooks";
import { useCreateBidContext } from "../CreateBidContext";

export const useReviewViewModel = () => {
  const [tenant, setTenant] = useState<ISubscriptionResponse | null>(null);
  const [materials, setMaterials] = useState<IMaterialsResponse[]>([]);

  const { form, getCustomerMaterials, communities } = useCreateBidContext();
  const { watch } = form;
  const {
    planAreas = [],
    requirements = [],
    builderId,
    communityId,
  } = watch();

  const community = communities.find((c) => c.id === communityId);
  const communityName = community?.name ?? "";
  const cityName = community?.city?.cityName ?? "";

  const {
    session: { tenantId },
  } = useSession();

  const stateManager = useComponentState();

  const getTenant = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);
      if (response.status === HTTP_STATUSES.OK) {
        setTenant(response.data);
      }
    });
  };

  const materialIds = useMemo(() => {
    const areaMaterialIds = planAreas.flatMap((area) =>
      area.materials?.map((material) => material.materialId)
    );
    const reqMaterialIds = requirements.flatMap((requirement) =>
      requirement.materials.map((material) => material.materialId)
    );

    const combinedIds = [...areaMaterialIds, ...reqMaterialIds];
    const ids = [...new Set(combinedIds)];

    return ids;
  }, []);

  const fetchMaterials = async () => {
    const response = await getCustomerMaterials({
      accountId: builderId,
      filters: { id: { in: { type: "guid", value: materialIds } } },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setMaterials(response.data);
    }
  };

  return {
    isLoading: stateManager.isBusy(),
    planAreas,
    requirements,
    tenant,
    materials,
    communityName,
    cityName,
    getTenant,
    fetchMaterials,
  };
};
